import * as i0 from '@angular/core';
import { Injectable, signal, SecurityContext, Pipe } from '@angular/core';
import { filter, take, map } from 'rxjs';
import * as i1$1 from '@angular/router';
import { NavigationEnd, RedirectCommand } from '@angular/router';
import * as i1 from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as i2 from '@angular/platform-browser';
class GenericCacheService {
  /**
   * Create a new cache
   * @param cacheDuration Time in minutes, which indicates how long objects should be cached.
   */
  constructor(cacheDuration = 5) {
    this.DEFAULT_KEY = 'DEFAULT';
    this._cache = {};
    this.CACHE_DURATION_IN_MINUTES = cacheDuration;
  }
  /**
   * Loads an object from the cache
   * @param key Key of the object to retrieve
   * @returns Returns null if there is no cached object.
   */
  getValue(key) {
    const keyValue = key ? this._cyrb53(typeof key === "string" ? key : JSON.stringify(key)).toString() : this.DEFAULT_KEY;
    const item = this._cache[keyValue];
    if (!item) {
      return null;
    }
    if (new Date() > item.expires) {
      return null;
    }
    return item.value;
  }
  /**
   * Stores an object to a key in the cache
   * @param value Object to be stored
   * @param key Key of the object
   */
  setValue(value, key) {
    const keyValue = key ? this._cyrb53(typeof key === "string" ? key : JSON.stringify(key)).toString() : this.DEFAULT_KEY;
    const expires = new Date(new Date().getTime() + this.CACHE_DURATION_IN_MINUTES * 60000);
    this._cache[keyValue] = {
      expires,
      value
    };
  }
  /** Clears this cache instance. */
  clearCache() {
    this._cache = {};
  }
  /*
    cyrb53 (c) 2018 bryc (github.com/bryc)
    License: Public domain (or MIT if needed). Attribution appreciated.
    A fast and simple 53-bit string hash function with decent collision resistance.
    Largely inspired by MurmurHash2/3, but with a focus on speed/simplicity.
  */
  _cyrb53(str, seed = 0) {
    let h1 = 0xdeadbeef ^ seed,
      h2 = 0x41c6ce57 ^ seed;
    for (let i = 0, ch; i < str.length; i++) {
      ch = str.charCodeAt(i);
      h1 = Math.imul(h1 ^ ch, 2654435761);
      h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 = Math.imul(h1 ^ h1 >>> 16, 2246822507);
    h1 ^= Math.imul(h2 ^ h2 >>> 13, 3266489909);
    h2 = Math.imul(h2 ^ h2 >>> 16, 2246822507);
    h2 ^= Math.imul(h1 ^ h1 >>> 13, 3266489909);
    return 4294967296 * (2097151 & h2) + (h1 >>> 0);
  }
}
let CookieService = /*#__PURE__*/(() => {
  class CookieService {
    constructor() {}
    /**
     * Sets a cookie for the current domain.
     * @param name Name of the cookies.
     * @param value Value of the cookies.
     * @param days Day at which the cookie expires
     */
    setDomainCookie(name, value, days = 365) {
      this.setCookieInternal(name, value, days, this.getHostname());
    }
    /**
     * Sets a cookie
     * @param name Name of the cookies.
     * @param value Value of the cookies.
     * @param days Day at which the cookie expires
     */
    setCookie(name, value, days = 365) {
      this.setCookieInternal(name, value, days, "");
    }
    getCookie(name) {
      let nameEQ = name + "=";
      let ca = document.cookie.split(';');
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) == ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
      }
      return null;
    }
    /**
     * Removes a cookie
     * @param name Name of the cookie to remove.
     */
    removeCookie(name) {
      document.cookie = name + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
    }
    getHostname() {
      if (location.hostname.includes("localhost")) return "localhost";
      return "." + location.hostname.split('.').reverse()[1] + "." + location.hostname.split('.').reverse()[0];
    }
    setCookieInternal(name, value, days, domain) {
      let expires = "";
      let domainStr = "";
      if (days) {
        let date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        expires = "; expires=" + date.toUTCString();
      }
      if (domain !== "") {
        domainStr = "; domain=" + domain;
      }
      document.cookie = name + "=" + (value || "") + expires + domainStr + "; path=/";
    }
    static {
      this.ɵfac = function CookieService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CookieService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CookieService,
        factory: CookieService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return CookieService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let TranslationHelperService = /*#__PURE__*/(() => {
  class TranslationHelperService {
    constructor(translateService, _cookieService, _router) {
      this.translateService = translateService;
      this._cookieService = _cookieService;
      this._router = _router;
      // Migrates the old 'setLang' to 'lang'.
      let lang = this._cookieService.getCookie("setLang");
      if (lang) {
        this._cookieService.setCookie("lang", lang, undefined);
        this._cookieService.removeCookie("setLang");
      } else
        // Either get the language cookie or the current browser language
        lang = this._cookieService.getCookie("lang") || this.parseLanguageCode(window.navigator.language);
      this._router.events.pipe(filter(ev => ev instanceof NavigationEnd), take(1)).subscribe(ev => {
        // Checks if the website was entered with the lang query applied.
        let tree = this._router.parseUrl(ev.url);
        if (tree.queryParams["lang"]) {
          let querylang = tree.queryParams["lang"];
          // Wordpress uses "cn" for Chinese, but angular uses "zh", this needs to be converted.
          if (querylang == "cn") querylang = "zh";
          lang = querylang;
        }
        this.translateService.use(lang);
      });
      this.translateService.setDefaultLang("en");
    }
    /** Adds all supported languages */
    addLangs(langs) {
      this.translateService.addLangs(langs);
    }
    /** Sets or gets the current language */
    set currentLang(val) {
      this.translateService.use(this.parseLanguageCode(val));
    }
    get currentLang() {
      return this.translateService.currentLang;
    }
    /**
     * Translates a string
     * @param transKey Tag to translate.
     * @returns Returns the tag or translation
     */
    translate(transKey) {
      return this.translateService.get(transKey).pipe(map(res => {
        let ret = res;
        if (!ret || ret == "") ret = transKey;
        return ret;
      }));
    }
    /**
     * @param code Browser Languagecode e.g. de-DE
     * @returns Returns the language
     */
    parseLanguageCode(code) {
      if (!code) return "en";
      let upperBounds = code.indexOf("-");
      return upperBounds > 0 ? code.substring(0, upperBounds) : code;
    }
    static {
      this.ɵfac = function TranslationHelperService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TranslationHelperService)(i0.ɵɵinject(i1.TranslateService), i0.ɵɵinject(CookieService), i0.ɵɵinject(i1$1.Router));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: TranslationHelperService,
        factory: TranslationHelperService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return TranslationHelperService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * The FilterStorage allows to store the state of page inside the url, so the user can reload the page as he likes
 * This service isn't available at root level an needs to be directly injected into a component.
 *
 * @usageNotes
 * ### Example
 * ```ts
 * ＠Component({
 *     ...
 *     providers: [FilterStorageService], // <-- Important!!!
 * })
 * export class MyComponent {
 *      ...
 *      constructor(
 *          ＠Inject(FilterStorageService) public filterStorage : FilterStorageService,
 *      ) {
 *          this.filterStorage.createProperties([{name: "myNewProperty", defaultValue: 0}]); // Declare a property, which will created during runtime
 *      }
 *
 *      ngOnInit() {
 *          let myVar = this.filterStorage["myNewProperty"](); // Access storage content
 *          this.filterStorage["myNewProperty"] = 5; // Set content
 *      }
 * }
 * ```
 */
let FilterStorageService = /*#__PURE__*/(() => {
  class FilterStorageService {
    constructor(_Route, _Router, _Sanitizer, _destroyRef) {
      this._Route = _Route;
      this._Router = _Router;
      this._Sanitizer = _Sanitizer;
      this._destroyRef = _destroyRef;
      this._Properties = {}; // Propertymetadata
    }
    /**
     * @param properties List of properties this storage has.
     */
    createProperties(properties) {
      const Thiz = this;
      for (const prop of properties) {
        // Declares the given property dynamically at runtime
        // to allow the change of the query, if the property changes.
        Object.defineProperty(this, prop.name, {
          get() {
            return Thiz._Properties[prop.name];
          },
          set(value) {
            // Everytime this property is written to, the query parameter will be updated
            Thiz._Properties[prop.name].set(value);
            Thiz._Router.navigate([], {
              relativeTo: Thiz._Route,
              queryParams: Thiz.buildQueryParameters(),
              queryParamsHandling: 'merge',
              replaceUrl: true
            });
          }
        });
        // Defines the default value as property.
        Object.defineProperty(this, prop.name + "Default", {
          value: prop.defaultValue,
          writable: false
        });
        this._Properties[prop.name] = signal(prop.defaultValue);
      }
      // Loads the query paramter on initialization
      this._Route.queryParams.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(res => {
        for (const iterator of Object.entries(res)) {
          if (this._Properties.hasOwnProperty(iterator[0])) {
            const sanitizedValue = this._Sanitizer.sanitize(SecurityContext.HTML, iterator[1]);
            this._Properties[iterator[0]].set(this.cast(sanitizedValue));
          }
        }
      });
    }
    buildQueryParameters() {
      let queryParams = {};
      for (const iterator of Object.entries(this._Properties)) {
        let value = "";
        if (iterator[1]() !== undefined) {
          // Arrays will be encoded as json.
          if (Array.isArray(iterator[1]())) value = JSON.stringify(iterator[1]());else value = iterator[1]();
        }
        queryParams[iterator[0]] = value;
      }
      return queryParams;
    }
    cast(value) {
      if (value.trim().startsWith("(") || value.trim().endsWith(")") || value.trim().length == 0 || value.trim().startsWith("+") || value.trim().startsWith("-")) return value;
      if (value.includes("&#")) {
        return value.replace(/&#([0-9]{1,3});/gi, function (_, numStr) {
          let num = parseInt(numStr, 10); // read num as normal number
          return String.fromCharCode(num);
        });
      }
      if (value.length > 0 && /[a-zA-Z]/.test(value[0])) return value;
      return JSON.parse(value);
    }
    static {
      this.ɵfac = function FilterStorageService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FilterStorageService)(i0.ɵɵinject(i1$1.ActivatedRoute), i0.ɵɵinject(i1$1.Router), i0.ɵɵinject(i2.DomSanitizer), i0.ɵɵinject(i0.DestroyRef));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: FilterStorageService,
        factory: FilterStorageService.ɵfac
      });
    }
  }
  return FilterStorageService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let RedirectGuardService = /*#__PURE__*/(() => {
  class RedirectGuardService {
    constructor(_router) {
      this._router = _router;
    }
    canActivate(route, _) {
      let data = route.data;
      if (!data.redirectTo) return new RedirectCommand(this._router.parseUrl("/404"));
      let queryFragment = Object.entries(route.queryParams).map(x => `${x[0]}=${x[1]}`).join("&");
      if (queryFragment) queryFragment = "?" + queryFragment;
      if (route.fragment) queryFragment += "#" + route.fragment;
      if (!data.paramMap) {
        let paths = data.redirectTo.split("/").filter(x => x);
        let url = "/" + paths.map(x => {
          if (x.startsWith(":")) return route.paramMap.get(x.slice(1));
          return x;
        }).join("/");
        url = url.replace(/\(/g, "%28").replace(/\)/g, "%29");
        return new RedirectCommand(this._router.parseUrl(url + queryFragment));
      }
      // If the parameters are named different, these will be mapped.
      let url = data.redirectTo;
      for (const param of Object.entries(data.paramMap)) url = url.replace(param[0], route.paramMap.get(param[1]));
      url = url.replace(/\(/g, "%28").replace(/\)/g, "%29");
      return new RedirectCommand(this._router.parseUrl(url + queryFragment));
    }
    static {
      this.ɵfac = function RedirectGuardService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || RedirectGuardService)(i0.ɵɵinject(i1$1.Router));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: RedirectGuardService,
        factory: RedirectGuardService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return RedirectGuardService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/** BBCode formatter pipe for better text formatting. */
let TextFormatterPipe = /*#__PURE__*/(() => {
  class TextFormatterPipe {
    transform(value, _args) {
      let result = "";
      let bulletList = [];
      let bulletBuffer = "";
      let isBulletList = false;
      let possibleBulletEnd = false;
      for (let i = 0; i < value.length; i++) {
        const c = value[i];
        switch (c) {
          case "-":
            {
              // NOSONAR
              isBulletList = true;
              possibleBulletEnd = false;
            }
            break;
          case "\\":
            {
              // NOSONAR
              i++;
              if (i < value.length) {
                switch (value[i]) {
                  case "-":
                  case "\\":
                  case "[":
                    {
                      // NOSONAR
                      if (isBulletList) bulletBuffer += value[i];else result += value[i];
                    }
                    break;
                  default:
                    {
                      // NOSONAR
                      if (isBulletList) {
                        bulletBuffer += c;
                        bulletBuffer += value[i];
                      } else {
                        result += c;
                        result += value[i];
                      }
                    }
                    break;
                }
                i++;
              } else result += c;
            }
            break;
          case '\n':
            {
              // NOSONAR
              if (isBulletList && !possibleBulletEnd) {
                bulletList.push("<li>" + bulletBuffer + "</li>");
                bulletBuffer = "";
                possibleBulletEnd = true;
              } else {
                // NOSONAR
                if (isBulletList) {
                  result += "<ul>" + bulletList.join("") + "</ul>";
                  bulletList = [];
                  isBulletList = false;
                  possibleBulletEnd = false;
                }
                result += "<br>";
              }
            }
            break;
          case '[':
            {
              // NOSONAR
              i++;
              if (i < value.length) {
                switch (value[i]) {
                  case "b":
                    {
                      // NOSONAR
                      result += "<strong>";
                      i++;
                    }
                    break;
                  case "i":
                    {
                      // NOSONAR
                      result += "<i>";
                      i++;
                    }
                    break;
                  case '/':
                    {
                      // NOSONAR
                      i++;
                      if (i < value.length) {
                        switch (value[i]) {
                          case "b":
                            {
                              // NOSONAR
                              result += "</strong>";
                            }
                            break;
                          case "i":
                            {
                              // NOSONAR
                              result += "</i>";
                            }
                            break;
                        }
                        i++;
                      } else result += "/";
                    }
                    break;
                  default:
                    {
                      // NOSONAR
                      result += c;
                    }
                    break;
                }
              } else result += c;
            }
            break;
          default:
            {
              // NOSONAR
              if (isBulletList && possibleBulletEnd) {
                result += "<ul>" + bulletList.join("") + "</ul>";
                bulletList = [];
                isBulletList = false;
                possibleBulletEnd = false;
                result += c;
              } else if (isBulletList) {
                bulletBuffer += c;
              } else result += c;
            }
            break;
        }
      }
      if (bulletList.length > 0) result += "<ul>" + bulletList.join("") + "</ul>";
      return result;
    }
    static {
      this.ɵfac = function TextFormatterPipe_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TextFormatterPipe)();
      };
    }
    static {
      this.ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
        name: "textFormatter",
        type: TextFormatterPipe,
        pure: true
      });
    }
  }
  return TextFormatterPipe;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * Public API Surface of utilities
 */

/**
 * Generated bundle index. Do not edit.
 */

export { CookieService, FilterStorageService, GenericCacheService, RedirectGuardService, TextFormatterPipe, TranslationHelperService };
